import type { CustomerServiceSlug } from '../contracts/customer-services.js';
import type { CustomerLifecycleStage } from '../contracts/customer-lifecycle.js';

/**
 * Shared ordering and completion-grouping for «درخواست‌های من».
 *
 * Both homes of the request list — the in-page region under the request form
 * and the «درخواست‌های من» bottom sheet — render through this module, so the
 * two can never disagree about what counts as completed or which request
 * comes first.
 *
 * ## Completion is canonical, never heuristic
 *
 * An entry is completed only when its already-resolved customer stage is one
 * of the two terminal stages, `completed` or `cancelled`. Those are the two
 * stored backend statuses an operator sets (`COMPLETED`, `CANCELLED`) as
 * mapped by `customerStageFor`; no status name, title text or page path is
 * inspected here. An unresolved (`null`) stage fails closed into the
 * incomplete group so an unknown state is never filed away as finished.
 *
 * ## Ordering never reclassifies
 *
 * The current service only affects order — its requests sort first — and
 * never moves a request between the incomplete and completed groups, nor
 * filters any service out. The sort is stable: within the same priority the
 * server order (newest first) is preserved.
 */

export interface RankableRequest {
  service: CustomerServiceSlug | string;
  stage: CustomerLifecycleStage | null;
}

/** True when the resolved customer stage is terminal. */
export function isCompletedStage(stage: CustomerLifecycleStage | null): boolean {
  return stage === 'completed' || stage === 'cancelled';
}

/** True when the entry belongs in the completed section. */
export function isCompletedRequest(entry: Pick<RankableRequest, 'stage'>): boolean {
  return isCompletedStage(entry.stage ?? null);
}

/**
 * Order entries for display: the current service first, everything else
 * after, each in its original (server) order.
 */
export function orderRequestsForService<T extends { service: CustomerServiceSlug | string }>(
  items: readonly T[],
  currentService: CustomerServiceSlug | string | null | undefined,
): T[] {
  if (!currentService) return [...items];
  const mine: T[] = [];
  const rest: T[] = [];
  for (const item of items) {
    if (item.service === currentService) mine.push(item);
    else rest.push(item);
  }
  return [...mine, ...rest];
}

export interface SplitRequests<T> {
  incomplete: T[];
  completed: T[];
}

/**
 * Split entries into the two displayed sections, ordering each one with the
 * current service first.
 */
export function splitMyRequests<T extends RankableRequest>(
  items: readonly T[],
  currentService: CustomerServiceSlug | string | null | undefined,
): SplitRequests<T> {
  const incomplete: T[] = [];
  const completed: T[] = [];
  for (const item of items) {
    if (isCompletedRequest(item)) completed.push(item);
    else incomplete.push(item);
  }
  return {
    incomplete: orderRequestsForService(incomplete, currentService),
    completed: orderRequestsForService(completed, currentService),
  };
}
